/**
 * TemplateUpdateNotice — a banner over the workspace when the open project was
 * built from an older (or a locally customized) version of a built-in template.
 * It offers to re-create the project from the current template as a new
 * project; the saved one is never rewritten. Dismissal is remembered per
 * project and per template version, so a later template change shows it again.
 */

import React, { useEffect, useState } from 'react';
import { useWorkspace } from './WorkspaceContext';
import ipc from '../../services/ipc';
import { checkTemplateStatus } from '../../services/templateVersion';

const isWeb = process.env.REACT_APP_WEB === '1';
const DISMISS_PREFIX = 'litecab.templateNotice.';

const loadBuiltin = (file) => ipc.invoke('load-template', file, { builtin: true });

function readDismissed(projectName) {
  try { return window.localStorage.getItem(DISMISS_PREFIX + projectName); } catch (_) { return null; }
}

function writeDismissed(projectName, key) {
  try { window.localStorage.setItem(DISMISS_PREFIX + projectName, key); } catch (_) { /* private mode etc. */ }
}

function message(status) {
  const name = status.entry.name;
  if (status.reason === 'pre-fix') {
    return {
      title: `Built from an older ${name} template.`,
      body: 'The 22 Sep 2026 fix removed a double count that overstated cell impacts about 5x and added an electrode design panel. This project still has the old inventory.',
    };
  }
  if (status.reason === 'custom-copy') {
    return {
      title: `Built from a customized copy of the ${name} template.`,
      body: `That copy was saved in ${isWeb ? 'this browser' : 'your templates folder'} and differs from the current built-in template.`,
    };
  }
  return {
    title: `Built from an older version of the ${name} template.`,
    body: 'The built-in template has been updated since this project was created.',
  };
}

export default function TemplateUpdateNotice() {
  const { project, handleRecreateFromTemplate } = useWorkspace();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const projectName = project?.project_info?.name || '';
  // Re-check when a different project (or template stamp) is open, not on every edit.
  const checkKey = [
    projectName,
    project?.template_id || '',
    project?.template_ref?.fingerprint || '',
    project?.design_parameters || project?.coating_parameters ? 'design' : '',
  ].join('|');

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    checkTemplateStatus(project, loadBuiltin)
      .then((s) => {
        if (cancelled || !s) return;
        if (readDismissed(projectName) === s.key) return;
        setStatus(s);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [checkKey]); // keyed on checkKey, not `project`, which changes on every edit

  if (!status) return null;
  const { title, body } = message(status);

  const recreate = async () => {
    setBusy(true);
    try { await handleRecreateFromTemplate(status.entry.template); }
    finally { setBusy(false); }
  };

  const dismiss = () => {
    writeDismissed(projectName, status.key);
    setStatus(null);
  };

  return (
    <div className="template-notice" role="status">
      <svg className="template-notice-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        <path d="M12 9v4" /><path d="M12 17h.01" />
      </svg>
      <div className="template-notice-text">
        <strong>{title}</strong> {body}
      </div>
      <div className="template-notice-actions">
        <button type="button" className="template-notice-btn" onClick={recreate} disabled={busy}>
          Re-create from current template
        </button>
        <button type="button" className="template-notice-dismiss" onClick={dismiss} title="Keep this version and hide this notice" aria-label="Dismiss">
          &times;
        </button>
      </div>
    </div>
  );
}
